import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Select,
  DatePicker,
  Tag,
  Alert,
  Spin,
} from "antd";
import { CopyOutlined, LinkOutlined } from "@ant-design/icons";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { API } from "../../../config/API";
import { GET } from "../../../utils/apirequest";
import {
  filterPlansForViewer,
  getUserRoleId,
} from "../../../utils/subscriptionPlans";
import axios from "../../../config/axiosInstance";
import {
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  currencyForCountry,
  formatMoney,
  missingPriceMessage,
  normalizeCountry,
  resolvePlanPrice,
} from "../../../config/countries";
import { useEpaperConfig } from "../../../config/epaperConfig";
import dayjs from "dayjs";

const paymentModes = [
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online (PayTabs)" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
];

type Plan = {
  id: number;
  plan_name: string;
  type: string;
  price: number | string;
  country_prices?: Record<string, { currency?: string; price: number | string }> | null;
  duration_type: "days" | "months" | "years";
  duration_value: number;
  region?: "india" | "gulf" | null;
  allowed_role_ids?: number[] | null;
};

interface EpaperUser {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  amount?: string | null;
  currency?: string | null;
  country?: string | null;
  subscriber_code?: string | null;
  subscription_end_date?: string | null;
  subscription_status?: string | null;
  current_plan_id?: number | null;
}

interface UpgradeModalProps {
  open: boolean;
  close: () => void;
  refresh: () => void;
  userData: EpaperUser | null;
}

const addPlanDuration = (start: dayjs.Dayjs, plan: Plan): dayjs.Dayjs => {
  const v = Number(plan.duration_value) || 0;
  if (!v) return start;
  switch (plan.duration_type) {
    case "days":
      return start.add(v, "day");
    case "months":
      return start.add(v, "month");
    case "years":
      return start.add(v, "year");
    default:
      return start;
  }
};

type SubScenario = "active" | "expired" | "cancelled" | "free";

// Resolve the real scenario for the user. subscription_status is the
// source of truth — cancelled/refunded must NOT be treated as "active"
// just because the end-date hasn't passed yet.
const resolveScenario = (user: EpaperUser | null): SubScenario => {
  if (!user) return "free";
  const status = (user.subscription_status || "").toLowerCase();
  if (status === "cancelled" || status === "refunded") return "cancelled";
  if (status === "expired") return "expired";
  // Free / none / pending / unset → no paid sub on file. If the user has
  // never been charged, treat as "free" (upgrade flow); otherwise inspect
  // the end-date to decide active-vs-expired.
  if (!user.amount && status !== "active") return "free";
  if (!user.subscription_end_date) return user.amount ? "expired" : "free";
  const end = dayjs(user.subscription_end_date);
  if (!end.isValid()) return "expired";
  return end.isAfter(dayjs()) ? "active" : "expired";
};

// Renewal start logic per scenario:
//   active     → extend from the existing end-date (no lost days)
//   expired    → start today
//   cancelled  → start today (this is a re-subscribe, not a chained renewal)
//   free       → start today (upgrade path)
const computeRenewalStart = (
  user: EpaperUser | null,
  scenario: SubScenario
): dayjs.Dayjs => {
  if (scenario !== "active") return dayjs();
  if (!user?.subscription_end_date) return dayjs();
  const end = dayjs(user.subscription_end_date);
  if (!end.isValid()) return dayjs();
  return end.isAfter(dayjs()) ? end : dayjs();
};

const UpgradeModal = (props: UpgradeModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  // Self-service payment link state — admin can hand the link to the user
  // instead of recording an offline payment via the form below.
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [linkExpiresAt, setLinkExpiresAt] = useState<string | null>(null);
  const User = useSelector((state: any) => state?.User);
  const viewerRoleId = getUserRoleId(User?.user);
  const { userData } = props;
  // Renewals are priced for the subscriber's own country (UAE when unset);
  // the backend applies the same rule.
  const userCountry = normalizeCountry(userData?.country) || DEFAULT_COUNTRY;
  const [currency, setCurrency] = useState(currencyForCountry(userCountry));
  // Payment links are charged in the subscriber's own currency (the server
  // signs the price into the link). There is no list of online currencies:
  // if PayTabs can't charge it, the subscriber is asked to contact an agent.
  const linkCurrency = currencyForCountry(userCountry);
  useEpaperConfig(props?.open);

  const scenario: SubScenario = useMemo(
    () => resolveScenario(userData),
    [
      userData?.subscription_status,
      userData?.subscription_end_date,
      userData?.amount,
    ]
  );
  const isRenewal = scenario !== "free";
  const isActive = scenario === "active";

  const daysRemaining = useMemo(() => {
    if (scenario !== "active" || !userData?.subscription_end_date) return null;
    const end = dayjs(userData.subscription_end_date);
    if (!end.isValid()) return null;
    return end.diff(dayjs(), "day");
  }, [scenario, userData?.subscription_end_date]);

  // Apply a plan: write into form (amount + dates). Pulled out so the
  // post-load auto-select and the user-driven select share one path.
  const applyPlan = (plan: Plan, source?: Plan[]) => {
    const list = source ?? plans;
    if (!plan || !list.find((p) => p.id === plan.id)) return;
    setSelectedPlanId(plan.id);
    const start = computeRenewalStart(userData, scenario);
    const priced = resolvePlanPrice(plan, userCountry);
    setCurrency(priced?.currency || DEFAULT_CURRENCY);
    form.setFieldsValue({
      subscription_plan_id: plan.id,
      // No price for this country → empty; the alert explains and the
      // backend refuses rather than charging AED.
      amount: priced?.missing ? undefined : priced?.amount ?? String(plan.price ?? 0),
      subscription_start_date: start,
      subscription_end_date: addPlanDuration(start, plan),
    });
  };

  // Fetch active plans whenever the modal opens; pre-select the user's
  // current plan if it's still in the list, otherwise the first plan.
  // Tracks plansError separately so "request failed" isn't conflated
  // with "no plans are configured" — the admin can't fix what they
  // can't identify.
  const loadPlans = async (signal?: { cancelled: boolean }) => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const params = new URLSearchParams({ page: "1", pageSize: "200" });
      const res: any = await GET(
        `${API.SUBSCRIPTION_PLANS}?${params.toString()}`,
        null,
        null
      );
      if (signal?.cancelled) return;
      if (res?.error) {
        setPlans([]);
        setPlansError(
          res.error.message || "The server rejected the plans request."
        );
        return;
      }
      if (!Array.isArray(res?.data)) {
        setPlans([]);
        setPlansError("Unexpected response from the plans endpoint.");
        return;
      }
      const rows: Plan[] = filterPlansForViewer(res.data, viewerRoleId);
      setPlans(rows);
      if (rows.length > 0) {
        const preferred = userData?.current_plan_id
          ? rows.find((p) => p.id === Number(userData.current_plan_id))
          : null;
        applyPlan(preferred || rows[0], rows);
      }
    } catch (err: any) {
      console.error("Failed to load subscription plans:", err);
      if (signal?.cancelled) return;
      setPlans([]);
      setPlansError(
        err?.message ||
          "Could not reach the plans endpoint. Check that the backend is running."
      );
    } finally {
      if (!signal?.cancelled) setPlansLoading(false);
    }
  };

  useEffect(() => {
    if (!props.open) return;
    const signal = { cancelled: false };
    loadPlans(signal);
    // Reset any link generated for a previous user.
    setLinkUrl(null);
    setLinkExpiresAt(null);
    return () => {
      signal.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, userData?.id]);

  const generatePaymentLink = async () => {
    if (!userData) return;
    // Validate the same form fields the offline-payment submit needs so the
    // admin's plan + dates + amount can be baked into the token. This makes
    // the user-facing /renew page a locked summary rather than a chooser.
    let values: any;
    try {
      values = await form.validateFields([
        "subscription_plan_id",
        "amount",
        "subscription_start_date",
        "subscription_end_date",
      ]);
    } catch {
      return;
    }
    setLinkLoading(true);
    try {
      const res: any = await axios.post(
        `${API.BASE_URL}epaper-users/${userData.id}/renewal-link`,
        {
          data: {
            subscription_plan_id: Number(values.subscription_plan_id),
            subscription_start_date: values.subscription_start_date
              ? values.subscription_start_date.toISOString()
              : undefined,
            subscription_end_date: values.subscription_end_date
              ? values.subscription_end_date.toISOString()
              : undefined,
            amount: String(values.amount ?? ""),
            // The admin generating the link is the "agent" of record for the
            // resulting subscription — same semantics as the offline-payment
            // submit below. The id rides inside the JWT and is used at
            // completion time to stamp agent_code/agent_name on the row.
            created_by: User?.user?.id,
          },
        }
      );
      const data = res?.data?.data;
      if (!data?.url) {
        message.error("Could not generate link.");
        return;
      }
      setLinkUrl(data.url);
      setLinkExpiresAt(data.expires_at || null);
    } catch (err: any) {
      message.error(
        err?.response?.data?.error?.message || "Failed to generate link."
      );
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      message.success("Link copied to clipboard");
    } catch {
      message.error("Could not copy. Please copy manually.");
    }
  };

  const handlePlanChange = (planId: number | undefined) => {
    if (planId == null) {
      setSelectedPlanId(null);
      return;
    }
    const plan = plans.find((p) => p.id === planId);
    if (plan) applyPlan(plan);
  };

  const handleSubmit = async (values: any) => {
    if (!userData) return;

    setLoading(true);
    try {
      const plan =
        values.subscription_plan_id != null
          ? plans.find((p) => p.id === Number(values.subscription_plan_id))
          : null;
      await axios.post(`${API.BASE_URL}epaper-users/${userData.id}/subscriptions`, {
        data: {
          amount:
            values.amount !== undefined && values.amount !== null && values.amount !== ""
              ? String(values.amount)
              : plan
                ? String(plan.price ?? 0)
                : "0",
          payment_mode: values.payment_mode,
          payment_date: values.payment_date
            ? values.payment_date.toISOString()
            : new Date().toISOString(),
          subscription_start_date: values.subscription_start_date
            ? values.subscription_start_date.toISOString()
            : new Date().toISOString(),
          subscription_end_date: values.subscription_end_date
            ? values.subscription_end_date.toISOString()
            : undefined,
          notes: values.notes || null,
          subscription_plan_id:
            values.subscription_plan_id != null
              ? Number(values.subscription_plan_id)
              : null,
          created_by: User?.user?.id,
        },
      });

      message.success(
        scenario === "free"
          ? `${userData.full_name} has been upgraded to premium!`
          : scenario === "cancelled"
            ? `${userData.full_name} has been re-subscribed!`
            : `Subscription renewed for ${userData.full_name}!`
      );
      form.resetFields();
      setSelectedPlanId(null);
      props?.refresh();
      props?.close();
    } catch (err: any) {
      const data = err?.response?.data;
      message.error(data?.error?.message || "Something went wrong!");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );
  const planPrice = resolvePlanPrice(selectedPlan, userCountry);

  // Banner copy keyed off the resolved scenario so the admin sees the right
  // story for cancelled / expired / active / first-time-paid users.
  const stateBanner = (() => {
    if (scenario === "free") {
      return {
        type: "info" as const,
        msg: "This user is on a free plan. Upgrading will create their first paid subscription starting today.",
      };
    }
    if (scenario === "cancelled") {
      return {
        type: "error" as const,
        msg: "Previous subscription was cancelled. The new term will start today.",
      };
    }
    if (scenario === "expired") {
      return {
        type: "warning" as const,
        msg: "Subscription has expired. The new term will start today.",
      };
    }
    // active
    const end = dayjs(userData?.subscription_end_date).format("DD-MM-YYYY");
    const remaining =
      daysRemaining != null && daysRemaining >= 0
        ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`
        : "";
    return {
      type: "success" as const,
      msg: `Active until ${end}${remaining ? ` (${remaining})` : ""}. Renewal will start from the current end date so paid days are not lost.`,
    };
  })();

  const modalTitle =
    scenario === "free"
      ? "Upgrade to Premium"
      : scenario === "cancelled"
        ? "Re-subscribe"
        : "Renew Subscription";
  const submitLabel =
    scenario === "free"
      ? "Upgrade to Premium"
      : scenario === "cancelled"
        ? "Re-subscribe"
        : "Renew Subscription";
  const headlineCopy =
    scenario === "free"
      ? `Upgrade ${userData?.full_name} from free to premium`
      : scenario === "cancelled"
        ? `Start a new subscription for ${userData?.full_name}`
        : `Renew subscription for ${userData?.full_name}`;

  const planSelectOptions = plans.map((plan) => ({
    value: plan.id,
    label: plan.plan_name,
  }));

  return (
    <Modal
      width="95%"
      style={{ maxWidth: 900, top: 20 }}
      title={modalTitle}
      open={props?.open}
      onCancel={() => {
        setSelectedPlanId(null);
        props?.close();
      }}
      footer={null}
      destroyOnClose
    >
      {plansLoading && plans.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : plans.length === 0 ? (
        plansError ? (
          <Alert
            type="error"
            showIcon
            message="Could not load subscription plans."
            description={
              <div>
                <div style={{ marginBottom: 8 }}>{plansError}</div>
                <Button size="small" onClick={() => loadPlans()}>
                  Retry
                </Button>
              </div>
            }
          />
        ) : (
          <Alert
            type="error"
            showIcon
            message="No active subscription plans available."
            description={`Create at least one active plan in Subscription Plans before ${isRenewal ? "renewing" : "upgrading"}.`}
          />
        )
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            payment_mode: "cash",
            payment_date: dayjs(),
          }}
          style={{ marginTop: 16 }}
        >
          <Row>
            {/* ── LEFT COLUMN: Subscriber Info ── */}
            <Col xs={12} md={6} style={{ paddingRight: 8, paddingLeft: 8 }}>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#0576b7",
                }}
              >
                Subscriber Info
              </div>

              <p style={{ marginBottom: 4 }}>{headlineCopy}</p>
              {userData?.subscriber_code && (
                <p style={{ margin: 0, color: "#0576b7", fontWeight: 600 }}>
                  Subscriber Code: {userData.subscriber_code}
                </p>
              )}
              {userData?.email && (
                <p style={{ margin: 0, color: "#666" }}>
                  Email: {userData.email}
                </p>
              )}
              {userData?.phone && (
                <p style={{ margin: 0, color: "#666" }}>
                  Phone: {userData.phone}
                </p>
              )}
              {isRenewal && userData?.amount && (
                <p style={{ margin: "4px 0 0", color: "#666" }}>
                  Last paid: {formatMoney(userData.amount, userData.currency)}
                </p>
              )}

              <Alert
                type={stateBanner.type}
                message={stateBanner.msg}
                showIcon
                style={{ marginTop: 16 }}
              />
            </Col>

            {/* ── RIGHT COLUMN: Subscription & Payment ──
                Left border removed; on mobile it becomes a top border
                that looks out of place. The column header does the
                visual separation on its own. */}
            <Col xs={12} md={6} style={{ paddingLeft: 8, paddingRight: 8 }}>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#0576b7",
                }}
              >
                Subscription & Payment
              </div>

              <Form.Item
                label="Subscription Plan"
                name="subscription_plan_id"
                rules={[
                  {
                    required: true,
                    message: "Please select a subscription plan",
                  },
                ]}
              >
                <Select
                  showSearch
                  loading={plansLoading}
                  placeholder="Select a plan"
                  onChange={(val: number | undefined) => handlePlanChange(val)}
                  filterOption={(input, option) => {
                    const text = String((option as any)?.label ?? "");
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={planSelectOptions}
                />
              </Form.Item>

              <Form.Item
                label={isRenewal ? "Renewal Amount" : "Subscription Amount"}
                name="amount"
                rules={[{ required: true, message: "Amount is required" }]}
              >
                <Input prefix={currency} placeholder="0" readOnly />
              </Form.Item>
              {planPrice?.missing ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={missingPriceMessage(selectedPlan, userCountry)}
                />
              ) : null}

              <Row>
                <Col md={6} style={{ paddingRight: 8 }}>
                  <Form.Item
                    label="Subscription Start"
                    name="subscription_start_date"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      placeholder="Start date"
                      inputReadOnly
                      open={false}
                      allowClear={false}
                    />
                  </Form.Item>
                </Col>
                <Col md={6} style={{ paddingLeft: 8 }}>
                  <Form.Item
                    label="Subscription End"
                    name="subscription_end_date"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      placeholder="End date"
                      inputReadOnly
                      open={false}
                      allowClear={false}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col md={6} style={{ paddingRight: 8 }}>
                  <Form.Item
                    label="Payment Mode"
                    name="payment_mode"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Select placeholder="Select payment mode">
                      {paymentModes.map((mode) => (
                        <Select.Option key={mode.value} value={mode.value}>
                          {mode.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={6} style={{ paddingLeft: 8 }}>
                  <Form.Item label="Payment Date" name="payment_date">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      placeholder="Payment date"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Notes" name="notes">
                <Input.TextArea
                  rows={2}
                  allowClear
                  placeholder="Optional notes"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ── PAYTABS PAYMENT LINK ──
              Alternative to recording an offline payment above: generate a
              tokenized link the user can open themselves to pay online via
              PayTabs. The link points at /renew/<token> on the website where
              the user picks a plan and pays. Token is single-use per
              transaction (idempotent on tran_ref) and expires in 7 days. */}
          <div
            style={{
              borderTop: "1px dashed #e0e0e0",
              paddingTop: 16,
              marginTop: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontWeight: 600,
                color: "#fa8c16",
                marginBottom: 4,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <LinkOutlined />
              Or send a payment link
            </div>
            <div style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
              Skip recording a payment here and send {userData?.full_name} a
              link to pay online via PayTabs.
            </div>

            <div style={{ color: "#888", fontSize: 12, marginBottom: 12 }}>
              Charged in {linkCurrency}. If our PayTabs account can't take{" "}
              {linkCurrency}, the subscriber is asked to contact a Suprabhaatham
              agent instead.
            </div>

            {linkUrl ? (
              <div>
                <Input.TextArea
                  value={linkUrl}
                  readOnly
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    icon={<CopyOutlined />}
                    onClick={copyLink}
                  >
                    Copy Link
                  </Button>
                  <Button onClick={generatePaymentLink} loading={linkLoading}>
                    Regenerate
                  </Button>
                  {linkExpiresAt && (
                    <span style={{ color: "#888", fontSize: 12 }}>
                      Expires{" "}
                      {dayjs(linkExpiresAt).format("DD MMM YYYY, HH:mm")}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <Button
                icon={<LinkOutlined />}
                onClick={generatePaymentLink}
                loading={linkLoading}
              >
                Generate Payment Link
              </Button>
            )}
          </div>

          {/* ── FOOTER BUTTONS ── */}
          <div
            style={{
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
              marginTop: 8,
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button
              onClick={() => {
                setSelectedPlanId(null);
                props?.close();
              }}
              style={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!!planPrice?.missing}
              style={{ minWidth: 160 }}
            >
              {submitLabel}
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default UpgradeModal;
